import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { SurveyType } from '../../../../../shared/constants';
import {
  formatSurveyAttentionRange,
  surveyScoreToLabel,
} from '../../../../../shared/surveys';
import { dimGrey } from '../../../../../mainTheme';
import type { FacilitySurveyLowestScore } from '../../../../../store/facility/facilityTypes';
import CustomerCareDTable from '../../../../Shared/CustomerCareDTable';
import SurveyResponseChip from '../../../../Patients/PatientDetails/components/OverviewTab/SurveyResponseChip';
import {
  PATIENT_INSIGHTS_TAB,
  patientDetailsPath,
  type PatientDetailsLocationState,
} from '../../../../Patients/PatientDetails/patientDetailsLink';
import { lowestScoreDisplayName } from './facilitySurveyInsights';

interface LowestScoresTableProps {
  // Already restricted to the attention range (selectLowestScoreRows).
  rows: readonly FacilitySurveyLowestScore[];
  surveyType: SurveyType;
  pageSize?: number;
}

// The patients whose lowest answer in the window needs attention: name and
// that response, worst first. Clicking a row opens the patient's Insights tab
// on the same survey type.
const LowestScoresTable: React.FC<LowestScoresTableProps> = ({
  rows,
  surveyType,
  pageSize = 10,
}) => {
  const navigate = useNavigate();

  // Stable column definitions so the data grid does not reset column state
  // on every render.
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'patient', headerName: 'Patient', flex: 1 },
      {
        field: 'score',
        headerName: 'Response',
        flex: 1,
        renderCell: (params: any) => (
          <SurveyResponseChip
            response={
              params.row.label ??
              surveyScoreToLabel(surveyType, params.row.score) ??
              'N/A'
            }
            isCurrent
          />
        ),
      },
    ],
    [surveyType],
  );

  if (!rows.length) {
    return (
      <Typography
        data-testid="lowest-scores-empty"
        sx={{
          color: dimGrey,
          fontSize: '14px',
          fontWeight: 500,
          lineHeight: '20px',
          py: 2,
        }}
      >
        No patient answered {formatSurveyAttentionRange(surveyType)} in this
        window.
      </Typography>
    );
  }

  const tableRows = rows.map((row) => ({
    id: row.customer_id,
    patient: lowestScoreDisplayName(row),
    score: row.score,
    label: row.label,
  }));

  const openPatientInsights = (row: { id: number }) => {
    const state: PatientDetailsLocationState = {
      tab: PATIENT_INSIGHTS_TAB,
      surveyType,
    };
    navigate(patientDetailsPath(row.id), { state });
  };

  return (
    <CustomerCareDTable
      rows={tableRows}
      columns={columns}
      pageSize={pageSize}
      defaultSortField="score"
      defaultSortDirection="asc"
      hideViewAllButton
      onRowClick={openPatientInsights}
    />
  );
};

export default LowestScoresTable;
